import { z } from 'zod'

/**
 * The day a household's tax year closes, as a month and a day.
 *
 * A month and a day rather than a named jurisdiction, because a jurisdiction has
 * to be resolved back into a date at read time and a pinned record must never be
 * re-derived; because tax year ends move by legislation, so a country list is a
 * thing to be versioned rather than a fact; and because a company on a fiscal
 * year of its own board's choosing is named by no list at all.
 */
export const FISCAL_YEAR_END_SETTING_KEY = 'fiscalYearEnd'

export interface FiscalYearEnd {
  month: number
  day: number
}

/**
 * What every pin taken before this setting existed was read on. Left as the
 * fallback for an absent row rather than written at sign-up, so that a household
 * that has never been asked is not on record as having answered 31 December.
 */
export const DEFAULT_FISCAL_YEAR_END: FiscalYearEnd = { month: 12, day: 31 }

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

/** A year that is not a leap year, so 29 February is measured against a February without one. */
const NON_LEAP = 2001

function lengthOf(month: number): number {
  return new Date(Date.UTC(NON_LEAP, month, 0)).getUTCDate()
}

export const fiscalYearEndSchema = z
  .object({
    month: z.number().int().min(1).max(12),
    day: z.number().int().min(1).max(31),
  })
  .superRefine(({ month, day }, ctx) => {
    if (day <= lengthOf(month)) return
    ctx.addIssue({
      code: 'custom',
      path: ['day'],
      // 29 February is refused for a reason the calendar alone does not give: a
      // pin is filed under the calendar year its date falls in, and a year end
      // that exists one year in four would be pinnable one year in four.
      message:
        month === 2 && day === 29
          ? 'a tax year cannot close on 29 February: three calendar years in four have no such day, and a year end has to fall in every one of them'
          : `${MONTHS[month - 1]} has ${lengthOf(month)} days`,
    })
  })

/**
 * The date a household's tax year closes in a given calendar year.
 *
 * The calendar year the close *falls in* is the key, which is what lets the
 * record go on being keyed by one integer: a year end with a fixed month and day
 * happens exactly once in every calendar year, so the key stays unique and
 * ordered however far the year behind it reaches back across the boundary.
 */
export function fiscalYearEndDate(end: FiscalYearEnd, year: number): string {
  return `${String(year).padStart(4, '0')}-${pad(end.month)}-${pad(end.day)}`
}

/**
 * What a household calls the year that closed on this date: "2025" where the tax
 * year is the calendar one, "2025/26" where it reaches back into the year before.
 *
 * Read off the pinned date rather than off the setting in force, because a
 * household that moves its year end must not find a record it already filed
 * relabelled under a year it was never read on.
 */
export function fiscalYearLabel(date: string): string {
  const year = Number(date.slice(0, 4))
  if (date.slice(5) === '12-31') return String(year)
  return `${year - 1}/${String(year).padStart(4, '0').slice(2)}`
}

/** What a month and a day come out as, for a card that has to show it. */
export function describeFiscalYearEnd(end: FiscalYearEnd): string {
  return `${end.day} ${MONTHS[end.month - 1]}`
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}
